import React, { useCallback, useEffect, useState } from 'react';
import { loadStripe, Stripe, StripeElementsOptions } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { AlertCircle, CreditCard, Loader2, Plus, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

export interface CustomerSavedCardRow {
  id: string;
  card_brand: string | null;
  card_last4: string | null;
  card_exp_month: number | null;
  card_exp_year: number | null;
}

interface CustomerSavedCardPanelProps {
  businessId: string;
  customerId: string;
  /** When false, show a short message instead of card UI */
  stripeConnectReady: boolean;
  customerEmail?: string | null;
  customerName?: string | null;
}

function unwrapInvokePayload<T extends Record<string, unknown>>(data: unknown): T | null {
  if (!data || typeof data !== 'object') return null;
  const o = data as Record<string, unknown>;
  if ('data' in o && o.data && typeof o.data === 'object') return o.data as T;
  return o as T;
}

function SavedCardForm({
  businessId,
  customerId,
  customerEmail,
  customerName,
  onSuccess,
  onCancel,
}: {
  businessId: string;
  customerId: string;
  customerEmail?: string | null;
  customerName?: string | null;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [complete, setComplete] = useState(false);

  const handleSave = async () => {
    if (!stripe || !elements) {
      setError('Payment form is not ready.');
      return;
    }
    const card = elements.getElement(CardElement);
    if (!card) {
      setError('Card field not found.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await supabase.auth.getSession();

      const { data: rawSetup, error: setupErr } = await supabase.functions.invoke('create-customer-card-setup', {
        body: { businessId, customerId },
      });
      if (setupErr) throw new Error(setupErr.message || 'Could not start card setup');
      const setup = unwrapInvokePayload<{ clientSecret?: string; setupIntentId?: string; error?: string }>(rawSetup);
      if (setup?.error) throw new Error(setup.error);
      const clientSecret = setup?.clientSecret;
      const setupIntentId = setup?.setupIntentId;
      if (!clientSecret || !setupIntentId) throw new Error('Invalid setup response from server');

      const { error: confirmError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card,
          billing_details: {
            email: customerEmail || undefined,
            name: customerName || undefined,
          },
        },
      });

      if (confirmError) throw new Error(confirmError.message);
      if (setupIntent?.status !== 'succeeded') {
        throw new Error(`Card setup was not completed (status: ${setupIntent?.status || 'unknown'})`);
      }

      const { data: rawFin, error: finErr } = await supabase.functions.invoke('finalize-customer-card-setup', {
        body: { businessId, customerId, setupIntentId },
      });
      if (finErr) throw new Error(finErr.message || 'Could not save card');
      const fin = unwrapInvokePayload<{ error?: string }>(rawFin);
      if (fin?.error) throw new Error(fin.error);

      toast.success('Card saved on file');
      onSuccess();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Something went wrong';
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const cardOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#374151',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        '::placeholder': { color: '#9CA3AF' },
      },
      invalid: { color: '#EF4444', iconColor: '#EF4444' },
    },
    hidePostalCode: true,
    disableLink: true,
  };

  return (
    <div className="bg-gray-50 rounded-xl p-5 border border-gray-200 space-y-4">
      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-1">Add card on file</h4>
        <p className="text-xs text-gray-500">
          Card is stored securely with Stripe. You can charge this card later from the app (where supported).
        </p>
      </div>
      <div className="px-4 py-3 bg-white border border-gray-300 rounded-xl focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-500">
        <CardElement
          options={cardOptions}
          onChange={(ev) => {
            setComplete(ev.complete);
            setError(ev.error?.message || null);
          }}
        />
      </div>
      {error && (
        <div className="flex items-start gap-2 text-sm text-red-600">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={busy || !stripe || !complete}
          className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Save card
        </button>
      </div>
    </div>
  );
}

function SavedCardFormWithStripe(
  props: {
    businessId: string;
    customerId: string;
    customerEmail?: string | null;
    customerName?: string | null;
    onSuccess: () => void;
    onCancel: () => void;
  },
) {
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [loading, setLoading] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await supabase.auth.getSession();
        const platformKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
        const { data: keyData, error: keyError } = await supabase.functions.invoke('get-stripe-connect-publishable-key', {
          body: { businessId: props.businessId, platformPublishableKey: platformKey },
        });
        const resolved = unwrapInvokePayload<{ publishableKey?: string; accountId?: string }>(keyData);
        if (keyError || !resolved?.publishableKey || !resolved?.accountId) {
          throw new Error('Could not load Stripe for this business.');
        }
        const stripe = await loadStripe(resolved.publishableKey, {
          stripeAccount: resolved.accountId,
        } as Parameters<typeof loadStripe>[1]);
        if (!cancelled) {
          setStripePromise(Promise.resolve(stripe));
          setLoading(false);
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setInitError(e instanceof Error ? e.message : 'Stripe failed to load');
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [props.businessId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6 text-sm text-gray-500 gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading secure form…
      </div>
    );
  }
  if (initError || !stripePromise) {
    return (
      <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl p-3">
        <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <span>{initError || 'Stripe unavailable'}</span>
      </div>
    );
  }

  const elementsOptions: StripeElementsOptions = {
    appearance: { theme: 'stripe' },
    loader: 'auto',
  };

  return (
    <Elements stripe={stripePromise} options={elementsOptions}>
      <SavedCardForm {...props} />
    </Elements>
  );
}

export default function CustomerSavedCardPanel({
  businessId,
  customerId,
  stripeConnectReady,
  customerEmail,
  customerName,
}: CustomerSavedCardPanelProps) {
  const [card, setCard] = useState<CustomerSavedCardRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [removing, setRemoving] = useState(false);

  const loadCard = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('customer_saved_cards')
        .select('id, card_brand, card_last4, card_exp_month, card_exp_year, card_funding')
        .eq('customer_id', customerId)
        .maybeSingle();

      if (error) throw error;
      setCard(data as CustomerSavedCardRow | null);
    } catch (e) {
      console.error('load saved card', e);
      setCard(null);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    loadCard();
  }, [loadCard]);

  const handleRemove = async () => {
    if (!card || removing) return;
    if (!window.confirm('Remove this card from the customer profile?')) return;
    setRemoving(true);
    try {
      await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke('remove-customer-saved-card', {
        body: { businessId, customerId },
      });
      const payload = unwrapInvokePayload<{ error?: string }>(data);
      if (error) throw new Error(error.message);
      if (payload?.error) throw new Error(payload.error);
      toast.success('Card removed');
      setCard(null);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Could not remove card');
    } finally {
      setRemoving(false);
    }
  };

  if (!stripeConnectReady) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/80 px-4 py-4 text-sm text-gray-600">
        <div className="flex items-start gap-3">
          <CreditCard className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-gray-800">Card on file</p>
            <p className="text-gray-500 mt-0.5">
              Finish Stripe onboarding, enable charges, and keep online payments on in Integrations to save a
              customer&apos;s card for later.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CreditCard className="h-4 w-4 text-blue-600" />
          <h4 className="text-sm font-semibold text-gray-900">Card on file</h4>
        </div>
        {!showAdd && card && (
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="text-xs font-semibold text-blue-600 hover:text-blue-700"
          >
            Replace
          </button>
        )}
        {!showAdd && !card && !loading && (
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 px-3 py-1.5 rounded-lg"
          >
            <Plus className="h-3.5 w-3.5" />
            Add card
          </button>
        )}
      </div>

      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-6 text-gray-500 text-sm gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading…
          </div>
        ) : showAdd ? (
          <SavedCardFormWithStripe
            businessId={businessId}
            customerId={customerId}
            customerEmail={customerEmail}
            customerName={customerName}
            onSuccess={() => {
              setShowAdd(false);
              loadCard();
            }}
            onCancel={() => setShowAdd(false)}
          />
        ) : card ? (
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                <CreditCard className="h-5 w-5 text-blue-600" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 capitalize truncate">
                  {(card.card_brand || 'Card').replace(/_/g, ' ')} •••• {card.card_last4 || '****'}
                </p>
                <p className="text-xs text-gray-500">
                  Exp{' '}
                  {card.card_exp_month != null && card.card_exp_year != null
                    ? `${String(card.card_exp_month).padStart(2, '0')}/${String(card.card_exp_year).slice(-2)}`
                    : '—'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleRemove}
              disabled={removing}
              className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 border border-red-100 rounded-lg disabled:opacity-50"
            >
              {removing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              Remove
            </button>
          </div>
        ) : (
          <p className="text-sm text-gray-500 text-center py-2">No card on file yet.</p>
        )}
      </div>
    </div>
  );
}
